import { getUUids, getSessionId } from './utils';
import { axiosServer } from './http';
import { getSign } from '@/api/utils';

const app_key = '1df869f2e620513484ddbd5521313a60';
/**
 * 打点事件名称
 */
export const EVENT_NAME = {
  ENTER_OFFICIAL_RECHARGE: '1', // 进入官充网页
  LOAD_COMPLETE: '2', // 加载完成
  ROLE_QUERY: '4', // 角色查询
  PRODUCT_CLICK: '5', // 商品点击
  ENTER_RECHARGE_CHANNEL: '6', // 进入充值渠道页
  CLICK_BUY: '7', // 点击购买-前后端打点
  // PAY_SUCCESS: '8', // 支付成功-后端打点
  // PAY_FAIL: '9', // 支付失败/取消-后端打点
  CRASH: '10', // 崩溃/页面报错
  CUSTOMER_GUIDE_EXPOSE: '11', // 客服引导曝光
  CUSTOMER_GUIDE_CLICK: '12' // 客服引导点击
};

const cacheKeyMap = new Map([[EVENT_NAME.ENTER_OFFICIAL_RECHARGE, 1]]);
const timerKeyMap = new Map([
  [EVENT_NAME.LOAD_COMPLETE, 2],
  [EVENT_NAME.CUSTOMER_GUIDE_EXPOSE, 11],
]);

const getCurrentPagePath = () => {
  if (typeof window === 'undefined' || !window.location) {
    return '';
  }
  const { hash, pathname } = window.location;
  if (hash) {
    const hashPath = hash.slice(1).split('?')[0];
    return hashPath || '/';
  }
  return pathname || '';
};

class EventReport {
  constructor() {
    this.timeMap = new Map();
    this.uuid = getUUids();
    let sid = sessionStorage.getItem('sid');
    if (!sid) {
      sid = getSessionId();
      sessionStorage.setItem('sid', sid);
    }
    this.sid = sid;
  }

  /**
   * 记录某个时间点，供后续耗时计算使用
   */
  setTime(key, time = Date.now()) {
    this.timeMap.set(key, time);
  }

  /**
   * 获取已缓存的时间点
   */
  getTime(key) {
    return this.timeMap.get(key);
  }

  /**
   * 记录进入官充页面的起始时间
   */
  setLaunchTime(time = Date.now()) {
    this.setTime(EVENT_NAME.ENTER_OFFICIAL_RECHARGE, time);
    if (!sessionStorage.getItem('launch_time')) {
      sessionStorage.setItem('launch_time', time);
    }
  }

  /**
   * 获取进入官充页面的起始时间
   */
  getLaunchTime() {
    const launchTime = sessionStorage.getItem('launch_time');
    return launchTime ? Number(launchTime) : this.getTime(EVENT_NAME.ENTER_OFFICIAL_RECHARGE);
  }

  /**
   * 清除已缓存的时间点
   */
  clearTime(key) {
    this.timeMap.delete(key);
  }

  /**
   * 计算某个起点到当前时刻的耗时，单位毫秒
   */
  getDuration(startKey, endTime = Date.now()) {
    const startTime = this.getTime(startKey);
    if (!startTime) return null;
    return endTime - startTime;
  }

  /**
   * 获取从进入官充页面开始到当前时刻的全流程耗时
   */
  getBaseEnterRechargeDuration(endTime = Date.now()) {
    const launchTime = this.getLaunchTime();
    if (!launchTime) return 0;
    return endTime - launchTime;
  }

  /**
   * 记录某个事件的开始时间
   * 常用于和 eventEnd 配套统计单事件耗时
   */
  eventStart(eventName, startTime = Date.now()) {
    if (eventName === EVENT_NAME.ENTER_OFFICIAL_RECHARGE) {
      this.setLaunchTime(startTime);
      return;
    }
    this.setTime(eventName, startTime);
  }

  /**
   * 上报单次事件耗时
   * 适用于“开始时不报，结束时一次性上报耗时”的场景
   */
  eventEnd(eventName, data = {}) {
    const endTime = Date.now();
    const name = data?.startEventName || eventName;
    const duration_ms = this.getDuration(name, endTime);
    const reportData = {
      ...data,
      duration_ms
    };

    return this.track(eventName, { ...reportData });
  }

  /**
   * 统一事件上报入口
   * 仅负责将内部字段映射成后端协议字段，并自动补充进入官充页时间戳与全流程耗时
   */
  track(eventName, data) {
    try {
      if (cacheKeyMap.has(eventName)) {
        this.eventStart(eventName, Date.now());
      }
      const currentTime = Date.now();
      const launchTime = this.getLaunchTime();
      const page = getCurrentPagePath();

      const roleInfo = JSON.parse(localStorage.getItem('roleInfo') || '{}');
      const params = {
        type: Number(eventName), // 事件类型，如 1=进入官充网页/2=加载完成
        game_id: data?.game_id || localStorage.getItem('game_id') || '', // 游戏id
        area: data?.area || localStorage.getItem('area') || '', // 语言版本，如 en、kr、jp
        from: data?.from || localStorage.getItem('from') || '', // 上报来源
        timestamp: Math.floor(currentTime / 1000), // 时间戳
        uuid: this.uuid || '', // 唯一标识
        sid: this.sid || '', // 会话id

        launch_time: launchTime, // 进入官充页时间戳，type=1

        url: page, // 页面地址, type=1/2

        ln_uid: data?.ln_uid || roleInfo?.uid || '', // 乐牛uid
        server_id: data?.server_id || roleInfo?.server_id || '', // 区服标识
        role_id: data?.role_id || roleInfo?.role_id || '', // 角色id
        role_name: data?.role_name || roleInfo?.role_name || '', // 角色名称

        event_result: data?.event_result || '', // 事件结果：type=2/4时，0不需要传 1成功 2失败 3无角色
        duration_ms: data?.duration_ms || '', // 事件耗时，毫秒，type=3/4/8
        product_id: data?.product_id || '', // 官网档位配置id，type=5/6/7/8/9
        product_type: data?.product_type || '', // 官网档位类型，1为currency 2为packs
        order_id: data?.order_id || '', // 订单号，type=7/8/9
        pay_channel: data?.pay_channel || '', // 支付方式，type=8/9
        pay_type: data?.pay_type || '', // 支付渠道，type=7

        fail_reason: data?.fail_reason || '', // 失败原因，type=9
        fail_type: data?.fail_type || '', // 错误类型，type=10
        fail_message: data?.fail_message || '', // 错误信息摘要，type=10

        fail_url: data?.fail_url || '' // 页面地址，type=10
      };
      const report = () => {
        axiosServer({
          url: 'officialRechargeReport',
          type: 'post',
          params: {
            ...params,
            sign: getSign(app_key, params)
          }
        }).catch(e => {
          console.log('eventReport err -----> ', e);
        });
      };
      if (timerKeyMap.has(eventName)) {
        setTimeout(() => {
          report();
        }, 0);
        return;
      }
      report();
    } catch (error) {
      console.log('eventReport error -----> ', error);
    }
  }
}

const eventReport = new EventReport();
export default eventReport;
